import { useState, useEffect } from 'react'
import {
  FileText,
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  File,
  FileSpreadsheet,
  FileCheck,
  TrendingUp,
  BarChart3,
} from 'lucide-react'
import { Select } from '../components/ui/Select'
import { PageHeader } from '../components/ui/PageHeader'
import { partnerAdminApi } from '../services/api'
import type {
  Document,
  DocumentsResponse,
  ProcessingStatus,
} from '../services/api'

/**
 * Documents Management Page for Partner Admin
 *
 * Purpose: Manage document processing and organization for partner's clients
 * Features:
 * - View all documents with filters
 * - Monitor processing status
 * - Bulk upload
 * - Templates management
 * - Analytics
 */
export function DocumentManagement() {
  const [documentsData, setDocumentsData] = useState<DocumentsResponse | null>(null)
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'documents' | 'processing'>('documents')

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  })
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadData()
  }, [page, filters])

  const loadData = async () => {
    setLoading(true)
    setError(null)

    try {
      const [docsResponse, statusResponse] = await Promise.all([
        partnerAdminApi.documents.list({
          page,
          limit: 10,
          type: filters.type || undefined,
          status: filters.status || undefined,
          search: filters.search || undefined,
        }),
        partnerAdminApi.documents.getProcessingStatus()
      ])

      setDocumentsData(docsResponse)
      setProcessingStatus(statusResponse)
    } catch (err: any) {
      console.error('Error loading documents:', err)
      setError(err.response?.data?.message || 'Erro ao carregar documentos')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed': return 'bg-green-100 text-green-800'
      case 'processing': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processed': return <CheckCircle className="w-4 h-4" />
      case 'processing': return <RefreshCw className="w-4 h-4 animate-spin" />
      case 'pending': return <Clock className="w-4 h-4" />
      case 'failed': return <XCircle className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'processed': return 'Processado'
      case 'processing': return 'Processando'
      case 'pending': return 'Pendente'
      case 'failed': return 'Falhou'
      default: return status
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'invoice': return <FileSpreadsheet className="w-5 h-5 text-green-600" />
      case 'tax_document': return <FileCheck className="w-5 h-5 text-blue-600" />
      case 'medical_record': return <File className="w-5 h-5 text-purple-600" />
      case 'contract': return <FileText className="w-5 h-5 text-orange-600" />
      case 'report': return <BarChart3 className="w-5 h-5 text-cyan-600" />
      default: return <File className="w-5 h-5 text-gray-600" />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'invoice': return 'Nota Fiscal'
      case 'tax_document': return 'Documento Fiscal'
      case 'medical_record': return 'Prontuário'
      case 'contract': return 'Contrato'
      case 'report': return 'Relatório'
      default: return type
    }
  }

  const formatFileSize = (sizeInKB: number) => {
    if (sizeInKB < 1024) return `${sizeInKB} KB`
    return `${(sizeInKB / 1024).toFixed(1)} MB`
  }

  if (loading && !documentsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Processamento de Documentos"
        subtitle="Gerencie e processe documentos dos seus clientes"
        actions={
          <button
            onClick={loadData}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        }
      />

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            ×
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {processingStatus?.overview?.totalDocuments || 0}
              </p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processados</p>
              <p className="text-2xl font-bold text-green-600">
                {processingStatus?.overview?.completed || 0}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processando</p>
              <p className="text-2xl font-bold text-blue-600">
                {processingStatus?.overview?.processing || 0}
              </p>
            </div>
            <RefreshCw className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-600">
                {processingStatus?.overview?.pendingProcessing || 0}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Falhas</p>
              <p className="text-2xl font-bold text-red-600">
                {processingStatus?.overview?.failed || 0}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documentos
              </div>
            </button>
            <button
              onClick={() => setActiveTab('processing')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'processing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Status por Cliente
              </div>
            </button>

          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'documents' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar documentos..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <Select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  options={[
                    { value: '', label: 'Todos os tipos' },
                    { value: 'invoice', label: 'Nota Fiscal' },
                    { value: 'tax_document', label: 'Documento Fiscal' },
                    { value: 'medical_record', label: 'Prontuário' },
                    { value: 'contract', label: 'Contrato' },
                    { value: 'report', label: 'Relatório' }
                  ]}
                />
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  options={[
                    { value: '', label: 'Todos os status' },
                    { value: 'processed', label: 'Processado' },
                    { value: 'processing', label: 'Processando' },
                    { value: 'pending', label: 'Pendente' },
                    { value: 'failed', label: 'Falhou' }
                  ]}
                />
              </div>

              {/* Summary */}
              {documentsData?.summary && (
                <div className="flex gap-4 p-3 bg-gray-50 rounded-lg text-sm">
                  <span className="text-gray-600">
                    <strong>{documentsData.summary.total}</strong> documentos
                  </span>
                  <span className="text-gray-400">|</span>
                  <span className="text-green-600">
                    <strong>{documentsData.summary.byStatus.processed}</strong> processados
                  </span>
                  <span className="text-blue-600">
                    <strong>{documentsData.summary.byStatus.processing}</strong> processando
                  </span>
                  <span className="text-yellow-600">
                    <strong>{documentsData.summary.byStatus.pending}</strong> pendentes
                  </span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-600">
                    Total: <strong>{formatFileSize(documentsData.summary.totalSize)}</strong>
                  </span>
                </div>
              )}

              {/* Documents Table */}
              {!documentsData?.documents || documentsData.documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum documento encontrado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Documento
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Tipo
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Cliente
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Data Upload
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Tamanho
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>

                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {documentsData.documents.map((doc) => (
                        <tr key={doc.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {getTypeIcon(doc.type)}
                              <div>
                                <p className="font-medium text-gray-900">{doc.name}</p>
                                <div className="flex gap-1 mt-1">
                                  {doc.tags.slice(0, 2).map((tag, i) => (
                                    <span
                                      key={i}
                                      className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {getTypeLabel(doc.type)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {doc.clientName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(doc.uploadedAt).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatFileSize(doc.size)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                              {getStatusIcon(doc.status)}
                              {getStatusLabel(doc.status)}
                            </span>
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {documentsData?.pagination && documentsData.pagination.totalPages > 1 && (
                <div className="flex justify-between items-center pt-4">
                  <p className="text-sm text-gray-600">
                    Página {documentsData.pagination.page} de {documentsData.pagination.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={!documentsData.pagination.hasPrev}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={!documentsData.pagination.hasNext}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'processing' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Status por Cliente</h3>

              {/* Client Processing Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {processingStatus?.byClient.map((client) => (
                  <div key={client.clientId} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">{client.clientName}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Concluídos</span>
                        <span className="font-medium text-green-600">{client.completed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Processando</span>
                        <span className="font-medium text-blue-600">{client.processing}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Pendentes</span>
                        <span className="font-medium text-yellow-600">{client.pending}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          Última atividade: {new Date(client.lastActivity).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Atividade Recente</h4>
                <div className="space-y-2">
                  {processingStatus?.recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      {activity.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.document}</p>
                        <p className="text-xs text-gray-500">{activity.client}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  )
}
